import { sha256 } from "./hashIdentity";

/**
 * Captura de consentimientos RGPD (contrato de privacidad Life5 v1, §5).
 *
 * Todo el módulo es inerte mientras `REACT_APP_CONSENT_CAPTURE` no sea "true":
 * ni se pinta la casilla de datos de salud ni se llama al endpoint de consents.
 * El backend (`POST /api/lead/{ulid}/consents`) lo construye otro equipo; si no
 * existe, la llamada falla en silencio y el funnel continúa.
 */

export type ConsentPurpose =
  | "privacy_policy"
  | "health_data"
  | "biometric"
  | "marketing"
  | "call_recording"
  | "ai_training";

export interface ConsentEntry {
  purpose: ConsentPurpose;
  granted: boolean;
  textHash: string;
}

export interface ConsentsPayload {
  policyVersion: string;
  consents: ConsentEntry[];
}

const HEALTH_CONSENT_KEY_PREFIX = "l5.consent.health_data.";
const PENDING_CONSENTS_KEY_PREFIX = "l5.consent.pending.";

export const isConsentCaptureEnabled = (): boolean =>
  process.env.REACT_APP_CONSENT_CAPTURE === "true";

export const getPrivacyPolicyVersion = (): string =>
  process.env.REACT_APP_PRIVACY_POLICY_VERSION || "";

/**
 * Deja el texto tal y como lo lee el usuario: sin marcado HTML y con los
 * espacios colapsados, para que el hash sea estable entre render y backend.
 */
export const normalizeConsentText = (text: string): string =>
  text
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const hashConsentText = async (text: string): Promise<string> =>
  sha256(normalizeConsentText(text));

const isDevelopment = (): boolean =>
  process.env.REACT_APP_ENVIROMENT
    ? process.env.REACT_APP_ENVIROMENT !== "PRD"
    : process.env.NODE_ENV === "development";

/** Traza solo en desarrollo y nunca con datos personales (solo propósitos y estado). */
export const logConsentDev = (message: string, detail?: unknown): void => {
  if (!isDevelopment()) return;
  // eslint-disable-next-line no-console
  console.info(`[consent] ${message}`, detail ?? "");
};

const safeSession = {
  get(key: string): string | null {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      // sin sessionStorage (modo privado, SSR) el gate simplemente vuelve a preguntar
    }
  },
  remove(key: string): void {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // idem
    }
  },
};

/** ¿Este lead ya consintió el tratamiento de datos de salud en esta sesión y versión de política? */
export const hasHealthConsentInSession = (leadId?: string): boolean =>
  !!leadId &&
  safeSession.get(`${HEALTH_CONSENT_KEY_PREFIX}${leadId}`) === getPrivacyPolicyVersion();

export const markHealthConsentInSession = (leadId?: string): void => {
  if (!leadId) return;
  safeSession.set(`${HEALTH_CONSENT_KEY_PREFIX}${leadId}`, getPrivacyPolicyVersion());
};

/**
 * Consentimientos otorgados antes de disponer del token del lead (casillas del
 * calculador). Se guardan por lead y se envían en cuanto hay token.
 */
export const stashPendingConsents = (leadId: string, consents: ConsentEntry[]): void => {
  if (!leadId || 0 === consents.length) return;
  safeSession.set(`${PENDING_CONSENTS_KEY_PREFIX}${leadId}`, JSON.stringify(consents));
};

export const takePendingConsents = (leadId?: string): ConsentEntry[] => {
  if (!leadId) return [];
  const key = `${PENDING_CONSENTS_KEY_PREFIX}${leadId}`;
  const raw = safeSession.get(key);
  if (!raw) return [];
  safeSession.remove(key);
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ConsentEntry[]) : [];
  } catch {
    return [];
  }
};

export const restorePendingConsents = (leadId: string, consents: ConsentEntry[]): void =>
  stashPendingConsents(leadId, consents);

/** Construye una entrada de consentimiento a partir del texto mostrado al usuario. */
export const buildConsentEntry = async (
  purpose: ConsentPurpose,
  granted: boolean,
  displayedText: string
): Promise<ConsentEntry> => ({
  purpose,
  granted,
  textHash: await hashConsentText(displayedText),
});
